import { Stack, Typography } from "@mui/material";
import { useEffect, useState, useTransition } from "react";
import { useParams } from "react-router";
import { genericTranslationsK, useGenericTranslationsT } from "src/translation-hooks/generic-translations";
import { Button, IconButton, TooltipShortcut } from "@itk/mui-components";
import { DekoxIcon } from "src/components";
import { RootState } from "src/store";
import { connect } from "react-redux";
import { filterActions } from "./filter-store";
import { FilterLoader } from "./filter-loader";
import { AttributesFilter, CustomFilter, LabelsFilter } from "./components";
import { configuratorActions } from "../../configurator-store";
import { getTotalFiltersApplied, hasAttributeFilter } from "./filter-types";

const mapStateToProps = (state: RootState) => ({
  formData: state.pageReducer.combinedConfiguratorReducer.filterReducer.formData,
  appliedFilter: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.appliedFilter,
  appliedCustomFilters: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.appliedCustomFilters,
  attributesCatalog: state.pageReducer.combinedConfiguratorReducer.configuratorReducer.attributesCatalog
});

const ownActions = {
  resetFormData: filterActions.resetFormData,
  setAppliedFilter: configuratorActions.setAppliedFilter,
  setAppliedCustomFilters: configuratorActions.setAppliedCustomFilters,
  setActiveOptionsPanel: configuratorActions.setActiveOptionsPanel,
  getProductsRequest: configuratorActions.getProductsRequest
};

type Props = typeof ownActions & ReturnType<typeof mapStateToProps>;

const FilterBase = (props: Props) => {
  const { resetFormData, setAppliedFilter, formData, appliedFilter, appliedCustomFilters, setAppliedCustomFilters, setActiveOptionsPanel, attributesCatalog, getProductsRequest } = props;
  const { projectId = "" } = useParams<{ projectId: string }>();

  const genericTranslationsT = useGenericTranslationsT();

  // The attribute fields are one Autocomplete per catalog attribute, so mounting them is by far the
  // most expensive part of opening this panel. Deferring them to a low-priority render lets the panel
  // header and the three cheap filters paint first — same technique as the tab swap in configurator.tsx.
  const [, startTransition] = useTransition();
  const [showAttributeFilters, setShowAttributeFilters] = useState(false);
  useEffect(() => {
    startTransition(() => setShowAttributeFilters(true));
  }, []);

  const handleReset = () => {
    resetFormData();
    // Clearing the attribute filter drops productsState in the reducer, so the Products tab falls
    // straight back to the unfiltered configurationsState it already holds — no request needed.
    setAppliedFilter({ ...appliedFilter, attributes: [] });
    setAppliedCustomFilters([]);
  };

  const handleApply = () => {
    const newApplied = { ...appliedFilter, attributes: formData.attributes };
    setAppliedFilter(newApplied);
    setAppliedCustomFilters(formData.customFilters);
    // Filtering narrows this tab's view only — nothing was mutated, so the Configurations tab's
    // dataset stays valid and is not re-fetched. Custom filters are applied client-side, so they
    // need no request at all.
    if (hasAttributeFilter(newApplied)) getProductsRequest(projectId);
  };

  const totalFiltersApplied = getTotalFiltersApplied(appliedFilter, appliedCustomFilters);

  return (
    <Stack overflow="hidden" width="100%" py={2} flex={1} minHeight={0}>
      {/* Header */}
      <Stack flexShrink={0} px={3} direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" fontWeight={600}>
          {genericTranslationsT.filter} {totalFiltersApplied > 0 ? `(${totalFiltersApplied})` : ""}
        </Typography>
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Stack gap={1} direction="row" alignItems="center">
            <TooltipShortcut shortcut={{ action: handleApply, shortCutKey: "a", modifier: "alt" }}>
              <Button color="success" t={genericTranslationsT} k={genericTranslationsK} field="apply" variant="outlined" size="small" onClick={handleApply} />
            </TooltipShortcut>
            <TooltipShortcut shortcut={{ action: handleReset, shortCutKey: "n", modifier: "alt" }}>
              <Button color="error" t={genericTranslationsT} k={genericTranslationsK} field="reset" variant="text" size="small" onClick={handleReset} />
            </TooltipShortcut>
          </Stack>
          <TooltipShortcut shortcut={{ action: () => setActiveOptionsPanel(null), shortCutKey: "f", modifier: "alt" }}>
            <IconButton k={genericTranslationsK.close} size="small" onClick={() => setActiveOptionsPanel(null)}>
              <DekoxIcon icon="doubleArrowRight" fontSize="small" />
            </IconButton>
          </TooltipShortcut>
        </Stack>
      </Stack>
      {/* Content */}
      <Stack overflow="auto" flex={1} minHeight={0} gap={2} mt={2} px={3} py={2}>
        {attributesCatalog.loading === "is-loading" && <FilterLoader />}
        {attributesCatalog.loading === "has-loaded" && (
          <>
            <CustomFilter />
            <LabelsFilter />
            {showAttributeFilters && <AttributesFilter />}
          </>
        )}
      </Stack>
    </Stack>
  );
};

export const Filter = connect(mapStateToProps, ownActions)(FilterBase);
